import React from 'react';
import { Link } from 'react-router-dom';
import { MdDoubleArrow } from 'react-icons/md';
import { FaLeaf, FaBrain, FaBalanceScale } from 'react-icons/fa';
import WellnessImage from '../../assets/images/people/siv-renate-main-about-me-in-beautiful-norway.webp';
import { BOOKING_PAGE_URL, CONTACT_PAGE_URL } from '../../utils/Routes';

function WellnessInfoSection() {
  return (
    <section className='grid gap-y-8 px-8 py-8 xl:py-24 2xl:px-32 lg:px-16'>
      {/* Heading */}
      <div className='grid gap-y-4 lg:gap-y-6 justify-center'>
        <h3 className='font-titleFont text-2xl text-center border-b-2 border-solid border-colour2 dark:border-colour3 pb-2'>
          WELLNESS COACHING WITH SIV RENATE
        </h3>
        <h4 className='text-center lg:text-xl italic font-serif'>
          Mindset & Lifestyle Coaching · Drammen, Norway · Online Worldwide
        </h4>
      </div>

      {/* Main: Bio + Image */}
      <div className='grid lg:grid-cols-2 gap-8 dark:text-colour2'>
        {/* Bio + Benefits + CTA */}
        <section className='grid gap-6 bg-colour1 rounded-3xl shadow-md p-6'>
          <article className='grid gap-4'>
            <h5 className='text-xl font-semibold font-titleFont'>
              My Coaching Approach
            </h5>
            <p className='text-base opacity-90'>
              I’m <span className='font-semibold'>Siv Renate Skaufel</span>, and
              my mission is to help you break free from limits and create a
              lifestyle you truly love. My wellness coaching combines{' '}
              <span className='font-semibold'>mindset shifts</span>,{' '}
              <span className='font-semibold'>lifestyle changes</span>, and{' '}
              <span className='font-semibold'>weekly check-ins</span> so you
              feel supported every step of the way. Together, we’ll work on
              building clarity, balance, and long-term growth.
            </p>
          </article>

          {/* Benefits */}
          <ul className='grid gap-4'>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaLeaf className='text-colour5 text-xl' aria-hidden='true' />
              <div className='grid'>
                <span className='font-semibold'>Stress Relief</span>
                <span className='text-sm opacity-80'>
                  Simple grounding practices that ease overwhelm and calm your
                  nervous system.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBrain className='text-colour5 text-xl' aria-hidden='true' />
              <div className='grid'>
                <span className='font-semibold'>Mindset Clarity</span>
                <span className='text-sm opacity-80'>
                  Tools and perspective shifts that help you focus on what
                  really matters.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBalanceScale
                className='text-colour5 text-xl'
                aria-hidden='true'
              />
              <div className='grid'>
                <span className='font-semibold'>Lifestyle Balance</span>
                <span className='text-sm opacity-80'>
                  Routines that support your health, relationships, and
                  personal energy.
                </span>
              </div>
            </li>
          </ul>

          {/* CTA: Book coaching */}
          <div className='grid px-0 justify-center'>
            <Link
              to={BOOKING_PAGE_URL}
              className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
              aria-label='Book a wellness coaching session with Siv Renate'
              title='Book a wellness coaching session with Siv Renate'
              rel='bookmark'
            >
              <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
                <span>BOOK COACHING</span>
                <MdDoubleArrow
                  className='text-xl lg:text-2xl'
                  aria-hidden='true'
                />
              </div>
            </Link>
          </div>
        </section>

        {/* Image */}
        <section className='grid rounded-3xl overflow-hidden shadow-md'>
          <img
            src={WellnessImage}
            alt='Siv Renate Skaufel — wellness coach in Drammen, Norway'
            className='w-full h-full object-cover'
            loading='lazy'
            decoding='async'
          />
        </section>
      </div>

      {/* Coaching Details */}
      <div className='grid gap-6 pt-8'>
        <h5 className='text-xl font-semibold font-titleFont text-center'>
          Coaching Plans & Values
        </h5>

        <div className='grid lg:grid-cols-3 gap-6'>
          {/* Card 1 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Tailored Plans</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                Custom to your goals
              </span>
            </div>
            <p className='text-sm opacity-90'>
              Each plan is designed around your unique lifestyle, helping you
              grow with practical tools and achievable steps.
            </p>
          </article>

          {/* Card 2 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Weekly Check-ins</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                Consistent support
              </span>
            </div>
            <p className='text-sm opacity-90'>
              Coaching includes regular one-to-one sessions or group calls to
              keep you accountable and supported.
            </p>
          </article>

          {/* Card 3 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Accessible Options</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                Online & In-person
              </span>
            </div>
            <p className='text-sm opacity-90'>
              Available online worldwide, or in-person in Drammen — for
              individuals or groups.
            </p>
          </article>
        </div>

        {/* CTA: Contact for details */}
        <div className='grid px-0 justify-center'>
          <Link
            to={CONTACT_PAGE_URL}
            className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
            aria-label='Contact Siv Renate about wellness coaching'
            title='Contact Siv Renate about wellness coaching'
            rel='bookmark'
          >
            <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
              <span>CONTACT ME</span>
              <MdDoubleArrow
                className='text-xl lg:text-2xl'
                aria-hidden='true'
              />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default WellnessInfoSection;
